import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useListAllEvents } from "../api/events-api";
import CountdownBanner from "../components/dss/CountdownBanner";
import EventCard from "../components/events/EventCard";
import { BrandMark, LogoMark } from "../components/dss/LogoMark";
import { FadeIn, Stagger, StaggerItem, useEnterAnimation } from "../components/dss/Motion";
import { Skeleton } from "../components/dss/Skeleton";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

const HomePage: FC = () => {
  const { member } = useSupabaseAuth();
  const { data: events = [], isLoading } = useListAllEvents();
  const animateIn = useEnterAnimation(isLoading);
  const navigate = useNavigate();

  const nextConfirmed = [...events]
    .filter((e) => e.status === "confirmed")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];

  // Rendered instead of the empty *or* populated layout while the first
  // fetch is in flight, so neither one flashes on screen with wrong data
  // (e.g. the "no events" empty state) only to be replaced a moment later.
  if (isLoading) {
    return (
      <div data-scroll-container data-sticky-scroll className="flex-1 overflow-y-auto px-7">
        <div className="flex justify-between items-start pt-2">
          <div className="flex items-center gap-3">
            <Skeleton className="w-9 h-9 rounded-full" />
            <Skeleton className="w-32 h-5" />
          </div>
          <Skeleton className="w-9 h-9 rounded-full mt-1" />
        </div>

        {/* Matches CountdownBanner's rendered height so the swap doesn't
            shift the calendar below it. */}
        <Skeleton className="mt-5 h-[222px] rounded-lg" />

        <div className="flex items-center justify-between mt-7 pb-2.5">
          <Skeleton className="w-24 h-6" />
        </div>
        <div className="h-0.5 bg-ink" />

        {[0, 1, 2].map((i) => (
          <div key={i} className="py-4 flex items-start gap-4 border-b border-line last:border-b-0">
            <div className="w-14 flex-shrink-0">
              <Skeleton className="w-8 h-3" />
              <Skeleton className="w-9 h-8 mt-1.5" />
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-2 pt-0.5">
              <Skeleton className="w-3/4 h-4" />
              <Skeleton className="w-1/2 h-3" />
              <Skeleton className="w-20 h-5 mt-1 rounded-full" />
            </div>
            <Skeleton className="w-6 h-6 rounded-full mt-1" />
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex-1 flex flex-col px-7">
        <div className="flex gap-3 items-center pt-2">
          <LogoMark size={46} disc={false} />
          <div>
            <div className="font-display text-[22px] leading-[0.92] tracking-tight font-extrabold uppercase">
              Det Sydsvenske
              <br />
              Spejderkorps
            </div>
            <div className="font-mono text-[10px] tracking-[1.4px] text-ink3 mt-1.5">EST. MMXVIII</div>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="font-mono text-[11px] tracking-[2px] text-ink3 mb-5">
            — — — INTET PLANLAGT — — —
          </div>
          <div className="font-display font-extrabold text-[120px] leading-[0.9] tracking-tighter tabular-nums">
            00
          </div>
          <div className="font-mono text-[10.5px] tracking-wide text-ink3 mt-1.5">
            SAMLINGER PÅ KALENDEREN
          </div>
          <div className="text-[13px] text-ink2 mt-5 max-w-[260px] leading-relaxed">
            Når en vært indkalder en samling, dukker den op her — og du kan stemme på datoer.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-scroll-container data-sticky-scroll className="flex-1 overflow-y-auto px-7">
      <div className="flex justify-between items-start pt-2">
        <BrandMark mark={false} />
        <button
          onClick={() => navigate("/me")}
          className="w-9 h-9 rounded-full bg-paper2 border border-line-soft flex items-center justify-center text-base mt-1"
        >
          {member?.emoji}
        </button>
      </div>

      {nextConfirmed && (
        <FadeIn enabled={animateIn} className="mt-5">
          <CountdownBanner
            event={nextConfirmed}
            onClick={() => navigate(`/event/${nextConfirmed.id}`)}
          />
        </FadeIn>
      )}

      <div className="flex items-center justify-between mt-7 pb-2.5">
        <div className="font-display font-extrabold text-[22px] tracking-tight uppercase">Kalender</div>
      </div>

      <div className="h-0.5 bg-ink" />
      <Stagger enabled={animateIn}>
        {events.map((event) => (
          <StaggerItem key={event.id} className="border-b border-line last:border-b-0">
            <EventCard event={event} />
          </StaggerItem>
        ))}
      </Stagger>
      {/* Indkaldelse lives in the admin panel — the calendar is for reading. */}
      <div className="h-5" />
    </div>
  );
};

export default HomePage;
